/**
 * SanPlumb — Catálogo de aparatos para la UI (Fase 3)
 *
 * Capa de presentación que define QUÉ aparatos se muestran en el contador
 * Hunter, con qué cantidad por defecto y qué predominancia de sistema.
 *
 * Los valores de UG provienen de la tabla normativa (`constants/is010.ts`,
 * APARATOS_PRIVADO/PUBLICO) — este archivo NO los redefine, solo referencia
 * por id y agrega metadatos de UI (default count, sistema predominante).
 *
 * Separar UI de norma evita que un cambio cosmético toque la tabla IS.010.
 */

import { APARATOS_PRIVADO, APARATOS_PUBLICO } from './is010';
import type { AparatoUG } from './is010';

/** Sistema predominante de un aparato (para mostrar etiqueta "+ AC", etc.). */
export type SistemaPredominante = 'AF' | 'AC';

export interface AparatoUI {
  /** id que coincide con AparatoUG.id de la tabla normativa. */
  id: string;
  /** Nombre mostrado en la UI. */
  name: string;
  /** Cantidad inicial al cargar la app. */
  default: number;
  /** Sistema predominante (afecta solo la etiqueta visual). */
  mostUsed: SistemaPredominante;
}

/**
 * Lista de aparatos visibles en el contador Hunter, en orden de uso típico.
 * El campo `id` debe existir en APARATOS_PRIVADO para resolver sus UG.
 */
export const APARATOS_UI: readonly AparatoUI[] = [
  { id: 'inodoro_tanque', name: 'Inodoro c/tanque', default: 1, mostUsed: 'AF' },
  { id: 'inodoro_red', name: 'Inodoro tanque desc. reducida', default: 0, mostUsed: 'AF' },
  { id: 'inodoro_valv', name: 'Inodoro c/válvula semiaut. (fluxómetro)', default: 0, mostUsed: 'AF' },
  { id: 'lavatorio', name: 'Lavatorio', default: 1, mostUsed: 'AF' },
  { id: 'ducha', name: 'Ducha', default: 1, mostUsed: 'AC' },
  { id: 'tina', name: 'Tina / bañera', default: 0, mostUsed: 'AC' },
  { id: 'bidet', name: 'Bidet', default: 0, mostUsed: 'AF' },
  { id: 'lavadero', name: 'Lavadero de ropa', default: 0, mostUsed: 'AF' },
  { id: 'urinario', name: 'Urinario c/tanque', default: 0, mostUsed: 'AF' },
] as const;

/** Índice id → AparatoUG normativo (uso privado). */
const PRIV_BY_ID = new Map<string, AparatoUG>(APARATOS_PRIVADO.map((a) => [a.id, a]));
/** Índice id → AparatoUG normativo (uso público). */
const PUB_BY_ID = new Map<string, AparatoUG>(APARATOS_PUBLICO.map((a) => [a.id, a]));

/**
 * Devuelve los valores UG normativos de un aparato según el modo de uso.
 * Si el id no existe en la tabla del modo, devuelve ceros (defensivo).
 */
export function ugFor(id: string, mode: 'priv' | 'pub'): { ugAF: number; ugAC: number } {
  const tbl = mode === 'pub' ? PUB_BY_ID : PRIV_BY_ID;
  const a = tbl.get(id);
  if (!a) return { ugAF: 0, ugAC: 0 };
  return { ugAF: a.ug_af, ugAC: a.ug_ac };
}

/** Cantidades iniciales para el estado `counts`. */
export function initialCounts(): Record<string, number> {
  return Object.fromEntries(APARATOS_UI.map((a) => [a.id, a.default]));
}

/** Resultado del cálculo de Unidades de Gasto acumuladas. */
export interface UGResult {
  /** UG de la red de AGUA FRÍA (dimensiona la alimentación, convención IS.010). */
  af: number;
  /** UG de la red de AGUA CALIENTE. */
  ac: number;
  /** UG usado para dimensionar (= af, por convención). */
  ug: number;
}

/**
 * Suma las Unidades de Gasto de todos los aparatos según sus cantidades y
 * el modo de uso. La red fría se dimensiona por `af`; la caliente por `ac`.
 */
export function calcUG(counts: Record<string, number>, mode: 'priv' | 'pub'): UGResult {
  let af = 0;
  let ac = 0;
  for (const a of APARATOS_UI) {
    const c = counts[a.id] ?? 0;
    if (c <= 0) continue;
    const { ugAF, ugAC } = ugFor(a.id, mode);
    af += c * ugAF;
    ac += c * ugAC;
  }
  af = +af.toFixed(2);
  ac = +ac.toFixed(2);
  return { af, ac, ug: af };
}
